import React from "react";
import Link from "next/link";
import {
  DocLayout,
  DocBreadcrumb,
  DocTitle,
  DocSection,
  DocCard,
  DocAlert,
  DocCodeBlock,
  DocNavFooter,
} from "@/components/docs";
import { CheckCircleIcon, ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { docsColors } from "@/lib/docs-theme";

export default function ZendeskNotificationsPage() {
  const breadcrumbs = [
    { label: "Documentation", href: "/docs" },
    { label: "Notification Methods", href: "/docs/notifications" },
    { label: "Zendesk" },
  ];

  return (
    <DocLayout
      title="Zendesk Notifications - Uptime Monitor Documentation"
      description="Create and manage support tickets automatically when monitors change status. Zendesk integration with auto-solve, deduplication, and priority management."
      canonical="https://uptime-monitor.io/docs/notifications/zendesk"
    >
      <DocBreadcrumb items={breadcrumbs} />

      <DocTitle
        title="Zendesk Notifications"
        description="Create and manage support tickets automatically when monitors change status."
      />

      {/* Quick Start */}
      <DocSection title="Quick Start">
        <DocCard>
          <p className={`${docsColors.text.body} mb-4`}>
            Zendesk integration automatically creates support tickets when monitors go down. Each
            incident gets its own ticket with full details.
          </p>
          <ul className="space-y-2">
            <li className="flex items-start">
              <span className={`${docsColors.text.muted} mr-2`}>•</span>
              <span className={docsColors.text.body}>
                <strong className={docsColors.text.heading}>Monitor Down:</strong> Creates a new
                ticket with error details
              </span>
            </li>
            <li className="flex items-start">
              <span className={`${docsColors.text.muted} mr-2`}>•</span>
              <span className={docsColors.text.body}>
                <strong className={docsColors.text.heading}>Monitor Up:</strong> Can auto-solve the
                ticket or leave it open
              </span>
            </li>
            <li className="flex items-start">
              <span className={`${docsColors.text.muted} mr-2`}>•</span>
              <span className={docsColors.text.body}>
                <strong className={docsColors.text.heading}>One ticket per incident:</strong> No
                duplicates or spam
              </span>
            </li>
          </ul>
        </DocCard>
      </DocSection>

      {/* Setup */}
      <DocSection title="Setup">
        <DocCard>
          <h3 className={`text-lg font-semibold ${docsColors.text.heading} mb-4`}>Get API Token</h3>
          <ol className="space-y-2 mb-6">
            <li className={docsColors.text.body}>
              1. Go to Zendesk Admin Center →{" "}
              <strong className={docsColors.text.heading}>
                Apps and integrations → APIs → API tokens
              </strong>
            </li>
            <li className={docsColors.text.body}>
              2. Click{" "}
              <strong className={docsColors.text.heading}>&quot;Add API token&quot;</strong>
            </li>
            <li className={docsColors.text.body}>
              3. Copy the token immediately (won&apos;t be shown again)
            </li>
          </ol>

          <h3 className={`text-lg font-semibold ${docsColors.text.heading} mb-4`}>
            Configure in Uptime Monitor
          </h3>
          <ol className="space-y-2 mb-6">
            <li className={docsColors.text.body}>
              1. Go to{" "}
              <Link href="/dashboard/contacts" className={docsColors.link.primary}>
                Contacts
              </Link>{" "}
              → Add Contact → Select Zendesk
            </li>
            <li className={docsColors.text.body}>
              2. Enter:
              <ul className="mt-2 ml-6 space-y-1">
                <li>
                  • <strong className={docsColors.text.heading}>Subdomain:</strong> yourcompany
                  (from yourcompany.zendesk.com)
                </li>
                <li>
                  • <strong className={docsColors.text.heading}>Email:</strong> Your Zendesk login
                  email
                </li>
                <li>
                  • <strong className={docsColors.text.heading}>API Token:</strong> The token from
                  step 1
                </li>
              </ul>
            </li>
            <li className={docsColors.text.body}>3. Save and assign to monitors</li>
          </ol>
        </DocCard>
      </DocSection>

      {/* Configuration Options */}
      <DocSection title="Configuration Options">
        <DocCard>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className={`border-b ${docsColors.border.default}`}>
                  <th className={`text-left py-3 px-4 font-semibold ${docsColors.text.heading}`}>
                    Field
                  </th>
                  <th className={`text-left py-3 px-4 font-semibold ${docsColors.text.heading}`}>
                    Required
                  </th>
                  <th className={`text-left py-3 px-4 font-semibold ${docsColors.text.heading}`}>
                    Description
                  </th>
                </tr>
              </thead>
              <tbody className={`divide-y ${docsColors.border.muted}`}>
                <tr>
                  <td className="py-3 px-4">
                    <code className={`${docsColors.bg.code} px-1 rounded`}>Subdomain</code>
                  </td>
                  <td className={`py-3 px-4 ${docsColors.text.body}`}>Yes</td>
                  <td className={`py-3 px-4 ${docsColors.text.body}`}>
                    Your Zendesk subdomain (without .zendesk.com)
                  </td>
                </tr>
                <tr>
                  <td className="py-3 px-4">
                    <code className={`${docsColors.bg.code} px-1 rounded`}>Email</code>
                  </td>
                  <td className={`py-3 px-4 ${docsColors.text.body}`}>Yes</td>
                  <td className={`py-3 px-4 ${docsColors.text.body}`}>
                    Email address used to authenticate with Zendesk
                  </td>
                </tr>
                <tr>
                  <td className="py-3 px-4">
                    <code className={`${docsColors.bg.code} px-1 rounded`}>API Token</code>
                  </td>
                  <td className={`py-3 px-4 ${docsColors.text.body}`}>Yes</td>
                  <td className={`py-3 px-4 ${docsColors.text.body}`}>
                    Zendesk API token for authentication
                  </td>
                </tr>
                <tr>
                  <td className="py-3 px-4">
                    <code className={`${docsColors.bg.code} px-1 rounded`}>Priority</code>
                  </td>
                  <td className={`py-3 px-4 ${docsColors.text.body}`}>No</td>
                  <td className={`py-3 px-4 ${docsColors.text.body}`}>
                    Default ticket priority (Low, Normal, High, Urgent)
                  </td>
                </tr>
                <tr>
                  <td className="py-3 px-4">
                    <code className={`${docsColors.bg.code} px-1 rounded`}>Tags</code>
                  </td>
                  <td className={`py-3 px-4 ${docsColors.text.body}`}>No</td>
                  <td className={`py-3 px-4 ${docsColors.text.body}`}>
                    Comma-separated list of tags to add to tickets
                  </td>
                </tr>
                <tr>
                  <td className="py-3 px-4">
                    <code className={`${docsColors.bg.code} px-1 rounded`}>Auto-solve tickets</code>
                  </td>
                  <td className={`py-3 px-4 ${docsColors.text.body}`}>No</td>
                  <td className={`py-3 px-4 ${docsColors.text.body}`}>
                    Automatically solve tickets when monitors recover
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </DocCard>
      </DocSection>

      {/* What Gets Created */}
      <DocSection title="What Gets Created">
        <DocCard>
          <h3 className={`text-lg font-semibold ${docsColors.text.heading} mb-3`}>
            Ticket Contents
          </h3>
          <ul className="space-y-2 mb-6">
            <li className={docsColors.text.body}>
              • <strong className={docsColors.text.heading}>Subject:</strong> [Monitor Down] Monitor
              Name
            </li>
            <li className={docsColors.text.body}>
              • <strong className={docsColors.text.heading}>Description:</strong> Error details,
              URL, region, timestamp
            </li>
            <li className={docsColors.text.body}>
              • <strong className={docsColors.text.heading}>Tags:</strong> uptime-monitor,
              monitor-down, plus your custom tags
            </li>
            <li className={docsColors.text.body}>
              • <strong className={docsColors.text.heading}>Priority:</strong> Based on your
              configuration
            </li>
          </ul>

          <h3 className={`text-lg font-semibold ${docsColors.text.heading} mb-3`}>Behavior</h3>
          <ul className="space-y-2">
            <li className={docsColors.text.body}>
              • <strong className={docsColors.text.heading}>One ticket per incident</strong> - no
              duplicates
            </li>
            <li className={docsColors.text.body}>
              • <strong className={docsColors.text.heading}>Auto-solve on recovery</strong> - if
              enabled
            </li>
            <li className={docsColors.text.body}>
              • <strong className={docsColors.text.heading}>No spam</strong> - only notifies on
              state changes
            </li>
          </ul>
        </DocCard>
      </DocSection>

      {/* Tips */}
      <DocSection title="Tips">
        <DocCard>
          <ul className="space-y-3">
            <li className={docsColors.text.body}>
              • <strong className={docsColors.text.heading}>Use tags:</strong> Add environment tags
              (production, staging) for easy filtering
            </li>
            <li className={docsColors.text.body}>
              • <strong className={docsColors.text.heading}>Set priorities:</strong> Critical
              monitors should create urgent tickets
            </li>
            <li className={docsColors.text.body}>
              • <strong className={docsColors.text.heading}>Auto-solve wisely:</strong> Enable for
              non-critical monitors to reduce manual work
            </li>
            <li className={docsColors.text.body}>
              • <strong className={docsColors.text.heading}>Create Zendesk views:</strong> Filter by
              &quot;uptime-monitor&quot; tag to see all monitoring tickets
            </li>
          </ul>
        </DocCard>
      </DocSection>

      {/* SSL Certificates */}
      <DocSection title="SSL Certificates">
        <DocCard>
          <p className={`${docsColors.text.body} mb-3`}>
            Certificate expiration creates tickets with automatic priority:
          </p>
          <ul className="space-y-2">
            <li className={docsColors.text.body}>
              • <strong className={docsColors.text.heading}>≤3 days:</strong> Urgent priority
            </li>
            <li className={docsColors.text.body}>
              • <strong className={docsColors.text.heading}>4-7 days:</strong> High priority
            </li>
            <li className={docsColors.text.body}>
              • <strong className={docsColors.text.heading}>8-14 days:</strong> Normal priority
            </li>
          </ul>
        </DocCard>
      </DocSection>

      {/* Troubleshooting */}
      <DocSection title="Troubleshooting">
        <DocCard>
          <h3 className={`text-lg font-semibold ${docsColors.text.heading} mb-3`}>
            Authentication Errors (401)
          </h3>
          <ul className="space-y-2 mb-6">
            <li className={docsColors.text.body}>
              • Use your <strong className={docsColors.text.heading}>login email</strong>, not
              support@subdomain.zendesk.com
            </li>
            <li className={docsColors.text.body}>
              • Check API token is <strong className={docsColors.text.heading}>Active</strong> in
              Zendesk
            </li>
            <li className={docsColors.text.body}>
              • Subdomain should be just &quot;yourcompany&quot; not
              &quot;yourcompany.zendesk.com&quot;
            </li>
          </ul>

          <h3 className={`text-lg font-semibold ${docsColors.text.heading} mb-3`}>
            Tickets Not Appearing
          </h3>
          <ul className="space-y-2 mb-6">
            <li className={docsColors.text.body}>
              • Check Zendesk&apos;s{" "}
              <strong className={docsColors.text.heading}>suspended tickets</strong> queue
            </li>
            <li className={docsColors.text.body}>• Verify user has permission to create tickets</li>
            <li className={docsColors.text.body}>• Check spam filters</li>
          </ul>

          <h3 className={`text-lg font-semibold ${docsColors.text.heading} mb-3`}>
            Duplicate Tickets
          </h3>
          <p className={docsColors.text.body}>
            Each incident creates only one ticket. If you see duplicates, check that external_id is
            enabled in your Zendesk account.
          </p>
        </DocCard>
      </DocSection>

      <DocNavFooter
        prev={{ href: "/docs/notifications/opsgenie", text: "Opsgenie" }}
        next={{ href: "/docs/status-pages", text: "Status Pages" }}
      />
    </DocLayout>
  );
}
